import type { PropsWithChildren } from 'react';
import { useTranslation } from 'react-i18next';

import type { ListMyOffers } from '@cold-ambulance/client';
import { Skeleton } from '@cold-ambulance/shadcn';
import { cn } from '@cold-ambulance/shadcn';
import { formatDate, sumEquipmentQuantities } from '@cold-ambulance/ui';

export function RequestCardSkeleton() {
  return (
    <div className="flex items-center justify-between rounded-lg border bg-white p-8">
      <div className="space-y-5">
        <div className="flex items-center gap-2">
          <Skeleton className="h-5 w-5 rounded-sm" />
          <Skeleton className="h-4 w-40" />
        </div>
        <div className="flex items-center gap-2 text-sm">
          <Skeleton className="h-5 w-5 rounded-sm" />
          <Skeleton className="h-4 w-12" />
          <Skeleton className="size-2" />
          <Skeleton className="h-4 w-12" />
        </div>
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <Skeleton className="h-5 w-5 rounded-sm" />
          <Skeleton className="h-4 w-12" />
          <span className="flex items-center gap-0.5 rounded-md border bg-white">
            <Skeleton className="h-3 w-8" />
          </span>
          <span className="flex items-center gap-0.5 rounded-md border bg-white">
            <Skeleton className="h-3 w-8" />
          </span>
        </div>
      </div>
      <Skeleton className="h-11 w-28 rounded-md" />
    </div>
  );
}

function Background({
  children,
  className,
}: PropsWithChildren<{ className?: string }>) {
  return (
    <div
      className={cn(
        'bg-primary relative min-h-40 w-full overflow-hidden rounded-xl',
        'bg-[linear-gradient(90deg,#333E75_0%,#687EEF_100%)] text-white',
        className,
      )}
    >
      <div className="absolute -top-1/8 -left-1/2 h-[225%] w-full rounded-full bg-black/20"></div>
      <div className="px-14 py-8">{children}</div>
    </div>
  );
}

export function InProgressRequestCard({
  request,
}: {
  request: ListMyOffers['records'][number]['request'];
}) {
  const { t } = useTranslation();

  const ambulanceCount =
    request.ambulanceServiceWithDriver.reduce(
      (acc, service) =>
        acc + service.type1Amount + service.type2Amount + service.type3Amount,
      0,
    ) +
    request.ambulanceServiceWithoutDriver.reduce(
      (acc, service) =>
        acc + service.type1Amount + service.type2Amount + service.type3Amount,
      0,
    ) +
    request.ambulance4x4WithDriver.reduce(
      (acc, service) => acc + service.amount,
      0,
    ) +
    request.ambulance4x4WithoutDriver.reduce(
      (acc, service) => acc + service.amount,
      0,
    );
  const mobileClinicCount = request.mobileClinicService.reduce(
    (acc, service) => acc + service.amount,
    0,
  );
  const paramedicCount = request.requestParamedicService.reduce(
    (acc, service) => acc + service.amount,
    0,
  );
  const nurseCount = request.requestNurseService.reduce(
    (acc, service) => acc + service.amount,
    0,
  );
  const doctorCount = request.doctorService.reduce(
    (acc, service) => acc + service.amount,
    0,
  );

  return (
    <Background>
      <div className="flex items-center justify-between">
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <p className="text-lg font-bold text-white">
              {t('requests.inProgress', { name: request.projectName })}
            </p>
          </div>
          <div className="flex items-center gap-2 text-sm">
            {formatDate(request.scheduleStartDate)} -{' '}
            {formatDate(request.scheduleEndDate)}
          </div>
          <div className="flex items-center gap-2 text-sm">
            {ambulanceCount > 0 && (
              <>
                {ambulanceCount}
                <span>{t('services.ambulance')}</span>
              </>
            )}
            {(() => {
              const equipmentCount =
                request.ambulanceServiceWithDriver.reduce(
                  (acc, service) =>
                    acc +
                    sumEquipmentQuantities(service.type1Equipment) +
                    sumEquipmentQuantities(service.type2Equipment) +
                    sumEquipmentQuantities(service.type3Equipment),
                  0,
                ) +
                request.ambulanceServiceWithoutDriver.reduce(
                  (acc, service) =>
                    acc +
                    sumEquipmentQuantities(service.type1Equipment) +
                    sumEquipmentQuantities(service.type2Equipment) +
                    sumEquipmentQuantities(service.type3Equipment),
                  0,
                ) +
                request.ambulance4x4WithDriver.reduce(
                  (acc, service) => acc + sumEquipmentQuantities(service.equipment),
                  0,
                ) +
                request.ambulance4x4WithoutDriver.reduce(
                  (acc, service) => acc + sumEquipmentQuantities(service.equipment),
                  0,
                ) +
                request.mobileClinicService.reduce(
                  (acc, service) => acc + sumEquipmentQuantities(service.equipment),
                  0,
                );
              return equipmentCount > 0 ? (
                <span className="text-xs text-white/70">
                  ({equipmentCount} {t('services.equipment')})
                </span>
              ) : null;
            })()}
            {mobileClinicCount > 0 && (
              <span className="flex items-center gap-0.5 rounded-md bg-white/20 px-2 py-px">
                {mobileClinicCount}
                <span>{t('services.mobileClinic')}</span>
              </span>
            )}
            {paramedicCount > 0 && (
              <span className="flex items-center gap-0.5 rounded-md bg-white/20 px-2 py-px">
                {paramedicCount}
                <span>{t('services.paramedic')}</span>
              </span>
            )}
            {nurseCount > 0 && (
              <span className="flex items-center gap-0.5 rounded-md bg-white/20 px-2 py-px">
                {nurseCount}
                <span>{t('services.nurse')}</span>
              </span>
            )}
            {doctorCount > 0 && (
              <span className="flex items-center gap-0.5 rounded-md bg-white/20 px-2 py-px">
                {doctorCount}
                <span>{t('services.doctor')}</span>
              </span>
            )}
          </div>
        </div>
      </div>
    </Background>
  );
}
